import { useQuery } from '@tanstack/react-query';
import { Dayjs } from 'dayjs';
import { QueryKey } from '../../enums/QueryKey';
import { getDateRangeFromStartDateByAddingMonths, formatLastUploadDate } from '../../utils/dateUtils';
import {
    getLastCapacityReportUploadDate,
    getLastCapacityReportUploadUserAndDate,
    getLatestCapacityReportByWorktypes,
    getCapacityReport,
    getLastCapacityReportUploadDatePerLocation,
} from '../../services/capacityReport';

export function getCapacityReportQueryKey({
    date,
    location,
    plausibilityCheck,
    numOfMonthColumns,
}: {
    date: Dayjs;
    location: number;
    plausibilityCheck: string;
    numOfMonthColumns: number;
}) {
    const { dateFrom, dateTo } = getDateRangeFromStartDateByAddingMonths(date, numOfMonthColumns);

    return [
        QueryKey.CapacityReport,
        {
            dateFrom: dateFrom.toISOString(),
            dateTo: dateTo.toISOString(),
            location,
            plausibilityCheck,
        },
    ];
}

export function useCapacityReport({
    date,
    location,
    plausibilityCheck,
    numOfMonthColumns,
}: {
    date: Dayjs;
    location: number;
    plausibilityCheck: string;
    numOfMonthColumns: number;
}) {
    const { dateFrom, dateTo } = getDateRangeFromStartDateByAddingMonths(date, numOfMonthColumns);
    const dateFromStr = dateFrom.toISOString();
    const dateToStr = dateTo.toISOString();

    const queryKey = getCapacityReportQueryKey({ date, location, plausibilityCheck, numOfMonthColumns });

    const { data, refetch } = useQuery({
        queryKey,
        queryFn: () => getCapacityReport({ location, dateFrom: dateFromStr, dateTo: dateToStr, plausibilityCheck }),
        enabled: !!date && !!location,
        initialData: [],
    });

    return { data, refetch };
}

export function useLastCapacityReportUploadDate(location: number) {
    const { data } = useQuery({
        queryKey: [QueryKey.LastUploadDate + '-capacityReport', { location }],
        queryFn: () => getLastCapacityReportUploadDate(location),
        enabled: !!location,
    });

    return formatLastUploadDate(data);
}

export function useLastCapacityReportUploadUserAndDate(location: number) {
    const { data } = useQuery({
        queryKey: [QueryKey.LastUploadUserAndDate + '-capacityReport', { location }],
        queryFn: () => getLastCapacityReportUploadUserAndDate(location),
        enabled: !!location,
    });

    const { user, date, userId } = data || {};
    return { user, userId, date: date ? formatLastUploadDate(date) : '' };
}

export function useLatestCapacityReportsByWorktypes({
    location,
    user,
    showLastUploadInfo,
}: {
    location: number;
    user: number;
    showLastUploadInfo: boolean;
}) {
    const { data } = useQuery({
        queryKey: [QueryKey.LatestCapacityReportsByWorktypes, { location, user }],
        queryFn: () => getLatestCapacityReportByWorktypes({ location, user }),
        enabled: !!showLastUploadInfo && !!location && !!user,
    });

    return data || [];
}

export function useLastCapacityReportUploadDatePerLocation(location: number) {
    const { data } = useQuery({
        queryKey: [QueryKey.LastUploadDate + '-capacityReport-per-location', { location }],
        queryFn: () => getLastCapacityReportUploadDatePerLocation(location),
        initialData: [],
    });

    return [data];
}
